import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://researchagent-backend-dot-aibot-fc4fc.wn.r.appspot.com';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor to include the auth token in all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      // console.log(`Adding auth token to request: ${config.url}`);
    } else {
      console.warn(`No auth token found for request: ${config.url}`);
    }
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle authentication errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // If the error is due to an expired token (401) and we haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      console.log('Received 401, attempting to refresh token...');
      
      try {
        // Try to get a fresh token from Firebase
        // Import the auth instance from the firebase config
        const { auth } = await import('../config/firebase');
        const currentUser = auth.currentUser;
        
        if (currentUser) {
          const newToken = await currentUser.getIdToken(true);
          localStorage.setItem('token', newToken);
          console.log('Token refreshed successfully');
          
          // Update the authorization header
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        } else {
          console.error('No current user found when trying to refresh token');
          // Redirect to login page
          window.location.href = '/login';
          return Promise.reject(error);
        }
      } catch (refreshError) {
        console.error('Error refreshing token:', refreshError);
        // Redirect to login page
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

export interface ResearchRequest {
  topic: string;
  depth: number;
  focus_areas?: string[];
  excluded_areas?: string[];
}

export interface ResearchResponse {
  id: string;
  topic: string;
  status: string;
  message: string;
  estimated_completion_time?: string;
}

export interface ResearchStatus {
  id: string;
  topic: string;
  status: string;
  created_at: string;
  updated_at: string;
  message?: string;
  outline?: any;
  content?: string;
  summary?: string;
  references?: any[];
  error?: string;
}

export interface Section {
  id: string;
  title: string;
  content: string;
}

export interface SectionRewriteResponse {
  content: string;
}

export interface Report {
  id: string;
  topic: string;
  created_at: string;
  updated_at: string;
  status: string;
  outline?: {
    title?: string;
    introduction?: string;
    sections: Section[];
    conclusion?: string;
  };
  content?: string;
  summary?: string;
  references?: any[];
}

export interface CheckoutRequest {
  price_id: string;
}

export interface CheckoutResponse {
  sessionId: string;
  url: string;
}

export interface CreditsResponse {
  credits: number;
}

export const paymentService = {
  // Create a checkout session
  createCheckoutSession: async (priceId: string): Promise<CheckoutResponse> => {
    const response = await api.post('/api/payments/checkout', { price_id: priceId });
    return response.data;
  },

  // Get user credits
  getUserCredits: async (): Promise<CreditsResponse> => {
    const response = await api.get('api/payments/credits');
    return response.data;
  },
};

export const researchService = {
  // Start a new research
  startResearch: async (request: ResearchRequest): Promise<ResearchResponse> => {
    const response = await api.post('/api/research', request);
    return response.data;
  },

  // Get research status
  getResearchStatus: async (researchId: string): Promise<ResearchStatus> => {
    const response = await api.get(`/api/research/${researchId}`);
    return response.data;
  },

  // Get all research jobs
  getAllResearch: async (): Promise<ResearchStatus[]> => {
    const response = await api.get('/api/research');
    return response.data;
  },

  // Get all reports
  getAllReports: async (): Promise<Report[]> => {
    const response = await api.get('/api/reports');
    return response.data;
  },

  // Get a specific report
  getReport: async (reportId: string): Promise<Report> => {
    const response = await api.get(`/api/reports/${reportId}`);
    return response.data;
  },

  // Delete a report
  deleteReport: async (reportId: string): Promise<void> => {
    await api.delete(`/api/reports/${reportId}`);
  },

  // Update report title
  updateReportTitle: async (reportId: string, title: string): Promise<void> => {
    await api.patch(`/api/reports/${reportId}`, { title });
  },

  // Update report introduction
  updateReportIntroduction: async (reportId: string, introduction: string): Promise<void> => {
    await api.patch(`/api/reports/${reportId}`, { introduction });
  },

  // Update report summary
  updateReportSummary: async (reportId: string, summary: string): Promise<void> => {
    await api.patch(`/api/reports/${reportId}`, { summary });
  },

  // Update a section in a report
  updateSection: async (reportId: string, sectionIdx: number, content: string, title?: string): Promise<void> => {
    const payload: { content: string; title?: string } = { content };
    if (title !== undefined) {
      payload.title = title;
    }
    await api.patch(`/api/reports/${reportId}/sections/${sectionIdx}`, payload);
  },

  // Rewrite a section using LLM
  rewriteSection: async (reportId: string, sectionIdx: number, content: string): Promise<SectionRewriteResponse> => {
    console.log(`Calling rewrite API for report ${reportId}, section ${sectionIdx}`);
    console.log('Content being sent:', content.substring(0, 100) + (content.length > 100 ? '...' : ''));
    
    try {
      const response = await api.post(`/api/reports/${reportId}/sections/${sectionIdx}/rewrite`, { content });
      console.log('Rewrite API response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error in rewriteSection API call:', error);
      throw error;
    }
  },
};

export default api;
